import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import InventoryImage from '../components/InventoryImage';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { isAmxAdmin } from '../utils/auth';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const STEPS = ['Basics', 'Specifications', 'Integration', 'Capabilities', 'Review'];

const INITIAL_FORM = {
  business_unit_id: '',
  asset_name: '',
  description: '',
  asset_type: 'physical',
  asset_nature: 'physical',
  administrative_status: 'active',
  list_on_capabilities_explorer: false,
  manufacturer: '',
  model: '',
  serial_number: '',
  asset_code: '',
  asset_image_id: '',
  location_label: '',
  facility_id: '',
  integration_type: 'not_connected',
  integration_gateway_id: '',
  amx_connector_id: '',
  telemetry_topic: '',
  actions_topic: '',
  interaction_model_id: '',
  external_system: '',
  external_endpoint: '',
  integration_notes: '',
  capability_ids: [],
};

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function slugKey(value) {
  const key = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return key || 'specification';
}

function buildSpecifications(rows) {
  const fields = rows
    .map((row) => ({
      key: slugKey(row.key || row.label),
      label: String(row.label || row.key || '').trim(),
      value: String(row.value || '').trim(),
      unit: String(row.unit || '').trim(),
    }))
    .filter((row) => row.label || row.value);
  return fields.length ? { fields } : {};
}

export default function AssetCreatePage(props) {
  const { businessUnitId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fixedBusinessUnitId = businessUnitId || '';
  const returnTo = params.get('return_to') || '';
  const initialCapabilityId = params.get('capability_id') || '';

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    ...INITIAL_FORM,
    business_unit_id: fixedBusinessUnitId,
    capability_ids: initialCapabilityId ? [initialCapabilityId] : [],
  });
  const [specRows, setSpecRows] = useState([{ key: '', label: '', value: '', unit: '' }]);
  const [loadState, setLoadState] = useState({ loading: true, error: '' });
  const [assetTypes, setAssetTypes] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState('');

  const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    let active = true;
    async function loadFoundation() {
      setLoadState({ loading: true, error: '' });
      try {
        const [businessUnitResponse, assetTypeResponse] = await Promise.all([
          axiosInstance.get('/partners/user/business-units'),
          axiosInstance.get('/asset-types'),
        ]);
        if (!active) {
          return;
        }
        const allUnits = Array.isArray(businessUnitResponse.data?.business_units)
          ? businessUnitResponse.data.business_units
          : [];
        const manageableUnits = allUnits.filter((unit) => (
          isAmxAdmin(user) || canManageBusinessUnit(partnerProfile, unit.business_unit_id, unit.parent_partner_id)
        ));
        setBusinessUnits(manageableUnits);
        setAssetTypes(Array.isArray(assetTypeResponse.data?.asset_types) ? assetTypeResponse.data.asset_types : []);
        setForm((current) => ({
          ...current,
          business_unit_id: fixedBusinessUnitId || current.business_unit_id || manageableUnits[0]?.business_unit_id || '',
        }));
        setLoadState({ loading: false, error: '' });
      } catch (err) {
        if (!active) {
          return;
        }
        setLoadState({
          loading: false,
          error: err?.response?.data?.message || err?.message || 'Unable to load asset setup data.',
        });
      }
    }
    loadFoundation();
    return () => {
      active = false;
    };
  }, [fixedBusinessUnitId, partnerProfile, user]);

  useEffect(() => {
    let active = true;
    async function loadCapabilities() {
      if (!form.business_unit_id) {
        setCapabilities([]);
        return;
      }
      setCapabilitiesLoading(true);
      try {
        const response = await axiosInstance.get('/partners/user/capabilities', {
          params: { business_unit_id: form.business_unit_id },
        });
        if (!active) {
          return;
        }
        setCapabilities(Array.isArray(response.data?.capabilities) ? response.data.capabilities : []);
      } catch (_err) {
        if (active) {
          setCapabilities([]);
        }
      } finally {
        if (active) {
          setCapabilitiesLoading(false);
        }
      }
    }
    loadCapabilities();
    return () => {
      active = false;
    };
  }, [form.business_unit_id]);

  const selectedBusinessUnit = useMemo(
    () => businessUnits.find((unit) => unit.business_unit_id === form.business_unit_id) || null,
    [businessUnits, form.business_unit_id],
  );

  const selectedCapabilities = useMemo(() => {
    const ids = new Set(form.capability_ids);
    return capabilities.filter((capability) => ids.has(capability.capability_definition_id));
  }, [capabilities, form.capability_ids]);

  const handleImageUpload = async (file) => {
    if (!form.business_unit_id) {
      setError('Select a Business Unit before uploading an image.');
      return;
    }
    setImageUploading(true);
    setError('');
    try {
      const uploadResponse = await axiosInstance.post('/capability-media/upload', {
        partner_id: form.business_unit_id,
        filename: file.name,
        content_type: file.type || 'image/png',
        size_bytes: file.size,
        target_kind: 'asset',
      });
      const { url, fields, file_id: fileId } = uploadResponse?.data || {};
      if (!url || !fileId) {
        throw new Error('Image upload did not return a target URL.');
      }
      const uploadForm = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        uploadForm.append(key, value);
      });
      uploadForm.append('file', file);
      const s3Response = await fetch(url, { method: 'POST', body: uploadForm });
      if (!s3Response.ok) {
        throw new Error('Image upload failed.');
      }
      updateForm({ asset_image_id: fileId });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to upload image.');
    } finally {
      setImageUploading(false);
    }
  };

  const toggleCapability = (capabilityId) => {
    setForm((current) => {
      const selected = new Set(current.capability_ids);
      if (selected.has(capabilityId)) {
        selected.delete(capabilityId);
      } else {
        selected.add(capabilityId);
      }
      return { ...current, capability_ids: Array.from(selected) };
    });
  };

  const updateSpecRow = (index, patch) => {
    setSpecRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )));
  };

  const removeSpecRow = (index) => {
    setSpecRows((current) => current.filter((_row, rowIndex) => rowIndex !== index));
  };

  const validate = () => {
    if (!form.business_unit_id) {
      return 'Business Unit is required.';
    }
    if (!form.asset_name.trim()) {
      return 'Asset name is required.';
    }
    if (!form.asset_type) {
      return 'Asset type is required.';
    }
    return '';
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError('');
    const integrationMetadata = {
      external_system: form.external_system.trim() || undefined,
      endpoint: form.external_endpoint.trim() || undefined,
      notes: form.integration_notes.trim() || undefined,
    };
    const payload = {
      business_unit_id: form.business_unit_id,
      asset_name: form.asset_name.trim(),
      description: form.description.trim() || undefined,
      asset_type: form.asset_type,
      asset_nature: form.asset_nature,
      administrative_status: form.administrative_status,
      global_response: Boolean(form.list_on_capabilities_explorer),
      manufacturer: form.manufacturer.trim() || undefined,
      model: form.model.trim() || undefined,
      serial_number: form.serial_number.trim() || undefined,
      asset_code: form.asset_code.trim() || undefined,
      asset_image_id: form.asset_image_id || undefined,
      location_label: form.location_label.trim() || undefined,
      facility_id: form.facility_id.trim() || undefined,
      specifications: buildSpecifications(specRows),
      integration_type: form.integration_type,
      integration_gateway_id: form.integration_type === 'amx_gateway' && form.integration_gateway_id
        ? form.integration_gateway_id
        : undefined,
      amx_connector_id: form.integration_type === 'amx_gateway' ? form.amx_connector_id.trim() || undefined : undefined,
      telemetry_topic: form.integration_type === 'amx_gateway' ? form.telemetry_topic.trim() || undefined : undefined,
      actions_topic: form.integration_type === 'amx_gateway' ? form.actions_topic.trim() || undefined : undefined,
      interaction_model_id: form.integration_type === 'amx_gateway' ? form.interaction_model_id.trim() || undefined : undefined,
      integration_metadata: Object.fromEntries(Object.entries(integrationMetadata).filter(([, value]) => value)),
      capability_ids: form.capability_ids,
      properties: {
        list_on_capabilities_explorer: Boolean(form.list_on_capabilities_explorer),
      },
    };
    try {
      const endpoint = fixedBusinessUnitId
        ? `/partners/user/business-units/${encodeURIComponent(form.business_unit_id)}/assets`
        : '/assets';
      const response = await axiosInstance.post(endpoint, payload);
      const assetId = response.data?.asset_id;
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(assetId ? `/assets/${encodeURIComponent(assetId)}` : '/assets');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to create asset.');
    } finally {
      setSaving(false);
    }
  };

  const stepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack spacing={2}>
          <FormControl fullWidth size="small" disabled={Boolean(fixedBusinessUnitId)}>
            <InputLabel id="asset-business-unit-label">Business Unit</InputLabel>
            <Select
              labelId="asset-business-unit-label"
              label="Business Unit"
              value={form.business_unit_id}
              onChange={(event) => updateForm({ business_unit_id: event.target.value, capability_ids: [] })}
            >
              {businessUnits.map((unit) => (
                <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                  {unit.display_name || unit.business_unit_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              label="Asset Name"
              value={form.asset_name}
              onChange={(event) => updateForm({ asset_name: event.target.value })}
            />
            <TextField
              fullWidth
              label="Asset Code"
              value={form.asset_code}
              onChange={(event) => updateForm({ asset_code: event.target.value })}
            />
          </Stack>
          <TextField
            multiline
            minRows={3}
            label="Description"
            value={form.description}
            onChange={(event) => updateForm({ description: event.target.value })}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-type-label">Asset Type</InputLabel>
              <Select
                labelId="asset-type-label"
                label="Asset Type"
                value={form.asset_type}
                onChange={(event) => updateForm({ asset_type: event.target.value })}
              >
                {assetTypes.map((type) => (
                  <MenuItem key={type.asset_type_code} value={type.asset_type_code}>
                    {type.display_name || formatLabel(type.asset_type_code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-nature-label">Nature</InputLabel>
              <Select
                labelId="asset-nature-label"
                label="Nature"
                value={form.asset_nature}
                onChange={(event) => updateForm({ asset_nature: event.target.value })}
              >
                <MenuItem value="physical">Physical</MenuItem>
                <MenuItem value="digital">Digital</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-admin-status-label">Administrative Status</InputLabel>
              <Select
                labelId="asset-admin-status-label"
                label="Administrative Status"
                value={form.administrative_status}
                onChange={(event) => updateForm({ administrative_status: event.target.value })}
              >
                {['draft', 'active', 'inactive', 'retired'].map((status) => (
                  <MenuItem key={status} value={status}>{formatLabel(status)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(form.list_on_capabilities_explorer)}
                  onChange={(event) => updateForm({ list_on_capabilities_explorer: event.target.checked })}
                />
              )}
              label="List on Explorer"
            />
            <Typography variant="body2" color="text.secondary">
              Makes this asset discoverable in the Capabilities Explorer.
            </Typography>
          </Paper>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField fullWidth label="Manufacturer" value={form.manufacturer} onChange={(event) => updateForm({ manufacturer: event.target.value })} />
            <TextField fullWidth label="Model" value={form.model} onChange={(event) => updateForm({ model: event.target.value })} />
            <TextField fullWidth label="Serial Number" value={form.serial_number} onChange={(event) => updateForm({ serial_number: event.target.value })} />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <InventoryImage fileId={form.asset_image_id} alt={form.asset_name || 'Asset image'} height={96} sx={{ width: { xs: '100%', md: 160 } }} />
            <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />} disabled={imageUploading}>
              {imageUploading ? 'Uploading...' : 'Upload Image'}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) {
                    handleImageUpload(file);
                  }
                }}
              />
            </Button>
            {form.asset_image_id ? (
              <Button color="error" onClick={() => updateForm({ asset_image_id: '' })}>
                Clear Image
              </Button>
            ) : null}
          </Stack>
        </Stack>
      );
    }

    if (activeStep === 1) {
      return (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField fullWidth label="Location" value={form.location_label} onChange={(event) => updateForm({ location_label: event.target.value })} />
            <TextField fullWidth label="Facility" value={form.facility_id} onChange={(event) => updateForm({ facility_id: event.target.value })} />
          </Stack>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Specification</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specRows.map((row, index) => (
                  <TableRow key={`spec-${index}`}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={row.label}
                        onChange={(event) => updateSpecRow(index, { label: event.target.value, key: slugKey(event.target.value) })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField fullWidth size="small" label="Value" value={row.value} onChange={(event) => updateSpecRow(index, { value: event.target.value })} />
                    </TableCell>
                    <TableCell sx={{ width: 160 }}>
                      <TextField fullWidth size="small" label="Unit" value={row.unit} onChange={(event) => updateSpecRow(index, { unit: event.target.value })} />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 96 }}>
                      <Button color="error" disabled={specRows.length === 1} onClick={() => removeSpecRow(index)}>
                        <DeleteRoundedIcon fontSize="small" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() => setSpecRows((current) => [...current, { key: '', label: '', value: '', unit: '' }])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Specification
          </Button>
        </Stack>
      );
    }

    if (activeStep === 2) {
      return (
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="asset-integration-type-label">Integration</InputLabel>
            <Select
              labelId="asset-integration-type-label"
              label="Integration"
              value={form.integration_type}
              onChange={(event) => updateForm({ integration_type: event.target.value })}
            >
              <MenuItem value="not_connected">Not Connected</MenuItem>
              <MenuItem value="amx_gateway">AMX Gateway</MenuItem>
              <MenuItem value="external_integration">External Integration</MenuItem>
            </Select>
          </FormControl>
          {form.integration_type === 'amx_gateway' ? (
            <Stack spacing={2}>
              <TextField label="Gateway ID" value={form.integration_gateway_id} onChange={(event) => updateForm({ integration_gateway_id: event.target.value })} />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField fullWidth label="Connector ID" value={form.amx_connector_id} onChange={(event) => updateForm({ amx_connector_id: event.target.value })} />
                <TextField fullWidth label="Interaction Model ID" value={form.interaction_model_id} onChange={(event) => updateForm({ interaction_model_id: event.target.value })} />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField fullWidth label="Telemetry Topic" value={form.telemetry_topic} onChange={(event) => updateForm({ telemetry_topic: event.target.value })} />
                <TextField fullWidth label="Actions Topic" value={form.actions_topic} onChange={(event) => updateForm({ actions_topic: event.target.value })} />
              </Stack>
            </Stack>
          ) : null}
          {form.integration_type === 'external_integration' ? (
            <Stack spacing={2}>
              <TextField label="External System" value={form.external_system} onChange={(event) => updateForm({ external_system: event.target.value })} />
              <TextField label="Endpoint" value={form.external_endpoint} onChange={(event) => updateForm({ external_endpoint: event.target.value })} />
            </Stack>
          ) : null}
          <TextField
            multiline
            minRows={3}
            label="Integration Notes"
            value={form.integration_notes}
            onChange={(event) => updateForm({ integration_notes: event.target.value })}
          />
        </Stack>
      );
    }

    if (activeStep === 3) {
      return (
        <Stack spacing={2}>
          {capabilitiesLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">Loading capabilities...</Typography>
            </Stack>
          ) : capabilities.length ? (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Capability</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Need Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {capabilities.map((capability) => (
                    <TableRow key={capability.capability_definition_id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={form.capability_ids.includes(capability.capability_definition_id)}
                          onChange={() => toggleCapability(capability.capability_definition_id)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{capability.capability_name}</TableCell>
                      <TableCell><Chip size="small" label={formatLabel(capability.status || 'active')} /></TableCell>
                      <TableCell>{capability.category_display_name || capability.category_name || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">No capabilities are registered for this Business Unit.</Typography>
            </Paper>
          )}
          {form.business_unit_id ? (
            <Button
              component={RouterLink}
              to={`/business-units/${encodeURIComponent(form.business_unit_id)}/capabilities/new`}
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Create Capability
            </Button>
          ) : null}
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{form.asset_name || 'Untitled Asset'}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedBusinessUnit?.display_name || 'Business Unit'}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={formatLabel(form.asset_type)} />
              <Chip label={formatLabel(form.asset_nature)} variant="outlined" />
              <Chip label={formatLabel(form.integration_type)} variant="outlined" />
              <Chip
                label={form.list_on_capabilities_explorer ? 'Listed on Explorer' : 'Not listed on Explorer'}
                color={form.list_on_capabilities_explorer ? 'success' : 'default'}
                variant={form.list_on_capabilities_explorer ? 'filled' : 'outlined'}
              />
            </Stack>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableBody>
              {[
                ['Manufacturer', form.manufacturer],
                ['Model', form.model],
                ['Serial Number', form.serial_number],
                ['Location', form.location_label],
                ['Explorer Listing', form.list_on_capabilities_explorer ? 'Listed' : 'Not listed'],
                ['Capabilities', selectedCapabilities.map((capability) => capability.capability_name).join(', ')],
              ].map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ width: 220, fontWeight: 700 }}>{label}</TableCell>
                  <TableCell>{value || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
    );
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Assets', href: '/assets' },
              { label: 'New Asset' },
            ]}
          />

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Inventory2RoundedIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>New Asset</Typography>
              </Stack>
              <Button component={RouterLink} to={returnTo || '/assets'} startIcon={<ArrowBackRoundedIcon />} variant="outlined">
                Back
              </Button>
            </Stack>

            {loadState.loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading asset setup...</Typography>
              </Stack>
            ) : loadState.error ? (
              <Alert severity="error">{loadState.error}</Alert>
            ) : (
              <Stack spacing={3}>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                  <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {STEPS.map((step) => (
                      <Step key={step}>
                        <StepLabel>{step}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  {stepContent()}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackRoundedIcon />}
                      disabled={activeStep === 0 || saving}
                      onClick={() => setActiveStep((step) => Math.max(0, step - 1))}
                    >
                      Previous
                    </Button>
                    {activeStep < STEPS.length - 1 ? (
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        onClick={() => setActiveStep((step) => Math.min(STEPS.length - 1, step + 1))}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<SaveRoundedIcon />}
                        onClick={submit}
                        disabled={saving}
                      >
                        {saving ? 'Creating...' : 'Create Asset'}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
